import { loadGeneric, loadGenericByPerson } from '@medlogic/shared/ui/ui-generic-list';
import { ProcessService } from '@medlogic/shared/shared-data-access';
import { loadApp, setIsLoading } from '@medlogic/medlogic/medlogic-state';
import { ConfigJsonService, IProcessState, ITenant, IAppAddhereState, EnModulo, EnModuleMode, IModuleInfo, LogService, GlobalService, IModule, EnViewMode, ILogin } from '@medlogic/shared/shared-interfaces';
import { ProcessActionTypes, fetchErrorProcess, fetchSuccessProcess, setProcess } from './process.actions';
import { of, switchMap, withLatestFrom, catchError, toArray, mergeMap, map } from 'rxjs';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Injectable } from '@angular/core';

@Injectable()
export class ProcessEffects {

  constructor(
    private cnfJson: ConfigJsonService,
    private glb: GlobalService,
    private log: LogService,
    private actions$: Actions,
    private store: Store<IAppAddhereState | any>,
    private processSrv: ProcessService
  ) { }

  loadProcess$ = createEffect(() => this.actions$
    .pipe(
      ofType(ProcessActionTypes.LoadProcess),
      withLatestFrom(this.store),
      mergeMap(([_, state]) => {
        return this.processSrv.getDataProcess(state?.tenant?.isDebug)
          .pipe(
            map(m => (m ? { ...m, Label: this.firstUpperCase(m?.Label) } : null)),
            toArray(),
            this.addCustomModules$(state?.tenant?.selectedTenant, state?.tenant?.login),
            switchMap((allProcesses: IModule[]) => [
              allProcesses ? fetchSuccessProcess({ allProcesses }) : fetchErrorProcess(null),
              setIsLoading({ isLoading: false })
            ]),
            catchError((e: any) => {
              console.log(e);
              this.store.dispatch(setIsLoading({ isLoading: false }));

              return of(fetchErrorProcess(null));
            })
          );
      })
    )
  );

  loadProcessesMenu$ = createEffect(() => this.actions$
    .pipe(
      ofType(ProcessActionTypes.LoadProcessMenu),
      withLatestFrom(this.store),
      mergeMap(([_, state]) => {
        return this.processSrv.getDataProcess(state?.tenant?.isDebug, 'MENU')
          .pipe(
            map(m => (m ? { ...m, Label: this.firstUpperCase(m?.Label) } : null)),
            toArray(),
            switchMap((allProcesses: IModule[]) => [
              allProcesses ? fetchSuccessProcess({ allProcesses }) : fetchErrorProcess(null),
              setIsLoading({ isLoading: false })
            ]),
            catchError((e: any) => {
              console.log(e);
              this.store.dispatch(setIsLoading({ isLoading: false }));

              return of(fetchErrorProcess(null));
            })
          );
      }),
      catchError((e: any) => {
        console.log(e);
        this.store.dispatch(setIsLoading({ isLoading: false }));

        return of(fetchErrorProcess(null));
      })
    )
  );

  loadProcessesHTML5$ = createEffect(() => this.actions$
    .pipe(
      ofType(ProcessActionTypes.LoadProcessHTML5),
      withLatestFrom(this.store),
      mergeMap(([_, state]) => {
        return this.processSrv.getDataProcess(state?.tenant?.isDebug, 'HTML5')
          .pipe(
            map(m => (m ? { ...m, Label: this.firstUpperCase(m?.Label) } : null)),
            toArray(),
            this.addCustomModules$(state?.tenant?.selectedTenant, state?.tenant?.login),
            switchMap((allProcesses: IModule[]) => [
              allProcesses ? fetchSuccessProcess({ allProcesses }) : fetchErrorProcess(null),
              setIsLoading({ isLoading: false })
            ]),
            catchError((e: any) => {
              console.log(e);
              this.store.dispatch(setIsLoading({ isLoading: false }));

              return of(fetchErrorProcess(null));
            })
          );
      })
    )
  );

  setProcessAndLoadGeneric$ = createEffect(() => this.actions$
    .pipe(
      ofType(ProcessActionTypes.SetProcessAndLoadGeneric),
      switchMap((p: IProcessState) => {
        const configModule = this.cnfJson.modules?.find(f => f.id === p.selectedProcess?.ObjInfo?.AtividadeNo);
        const identification = configModule?.identification || null;

        return [
          setProcess({ selectedProcess: p?.selectedProcess }),
          loadGeneric({ genericAno: p?.selectedProcess?.ObjInfo?.AtividadeNo, identification, genericLabel: p?.selectedProcess?.Label }),
          loadApp({ title: p?.selectedProcess?.Label, isLoading: true, enViewMode: EnViewMode.browse }),
          setIsLoading({ isLoading: false })
        ];
      }),
      catchError((e: any) => {
        console.log(e);
        this.store.dispatch(setIsLoading({ isLoading: false }));

        return of(fetchErrorProcess(null));
      })
    )
  );

  setProcessAndLoadGenericByPerson$ = createEffect(() => this.actions$
    .pipe(
      ofType<any>(ProcessActionTypes.SetProcessAndLoadGenericByPerson),
      withLatestFrom(this.store),
      switchMap(([action, state]) => {
        const p = action?.selectedProcess;

        return [
          setProcess({ selectedProcess: p }),
          loadGenericByPerson({ genericAno: p?.ObjInfo?.AtividadeNo, genericLabel: p?.Label, person: state?.person?.person }),
          loadApp({ title: p?.Label, isLoading: true, enViewMode: EnViewMode.browse }),
          setIsLoading({ isLoading: false })
        ];
      }),
      catchError((e: any) => {
        console.log(e);
        this.store.dispatch(setIsLoading({ isLoading: false }));

        return of(fetchErrorProcess(null));
      })
    )
  );

  /**
   * Adiciona processos personalizados, que não vêm da API, e sim são definidos estaticamente no código. Atualmente, é utilizado para adicionar o processo de Prescrição, que é um processo derivado, ou seja, não tem um AtividadeNo específico, mas sim o AtividadeNo é preenchido dinamicamente com base no tenant selecionado.
   * @param selectedTenant Tenant selecionado, que contém os valores necessários para preencher o AtividadeNo dos processos personalizados.
   * @param login Informações do usuário logado, que contém os papéis do usuário, necessários para filtrar os processos personalizados, caso haja configuração de papéis para eles no config.json.
   * @returns Função de operador do RxJS que recebe a lista de processos vinda da API, adiciona os processos personalizados, e retorna a lista completa de processos. A função é utilizada no pipe dos efeitos de carregamento dos processos, para garantir que os processos personalizados sejam adicionados sempre que os processos forem carregados da API.
   */
  private addCustomModules$ = (selectedTenant: ITenant & { cadPrescriptionNo: number }, login: ILogin) => map((allProcesses: IModule[]) => {
    let staticProcesses = [
      {
        UrlIcon: `${this.cnfJson.imgBasePath}/Arquivos/Icon/266.png`,
        Label: 'Prescrição',
        Type: EnModulo.Processo,
        ObjInfo: { AtividadeNo: null, ProcessoNo: 1000, isCustom: true } as IModuleInfo,
        enModuleMode: EnModuleMode.singleRecord,
        IsActive: true,
      } as IModule,
    ];

    staticProcesses = staticProcesses.filter(f => this.filterAuthorizedProcesses(f, login));
    allProcesses = this.sortByComplementaryVar1PreserveNulls(allProcesses || []);

    this.fillDerivedFormId(staticProcesses, selectedTenant);

    return [...allProcesses, ...staticProcesses];
  });

  /**
   * Verifica se o processo é autorizado para o usuário logado, com base nos papéis do usuário e nos papéis permitidos para o processo, definidos no config.json.
   * @param process Processo a ser verificado.
   * @param login Informações do usuário logado, que contém os papéis do usuário.
   * @returns true se o processo for autorizado para o usuário, ou seja, se houver interseção entre os papéis do usuário e os papéis permitidos para o processo. Caso contrário, retorna false.
   */
  private filterAuthorizedProcesses(process: IModule, login: ILogin): boolean {
    try {
      const module = this.cnfJson.modules?.find(f => f.id === process.ObjInfo.ProcessoNo);

      return login?.roles?.findIndex(role =>
        module?.roles?.map(m => m.toUpperCase()).includes(role.toUpperCase())
      ) >= 0;
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'filterAuthorizedProcess', error.message);
    }

    return false;
  }

  /**
   * Preenche o AtividadeNo dos processos personalizados, com base no cadNoName definido no config.json e no valor presente no tenant selecionado.
   * @param staticProcesses Processos personalizados, que não vêm da API, e sim são definidos estaticamente no código.
   * @param selectedTenant Tenant selecionado, que contém os valores necessários para preencher o AtividadeNo dos processos personalizados.
   */
  private fillDerivedFormId(staticProcesses: IModule[], selectedTenant: ITenant & { cadPrescriptionNo: number }): void {
    try {
      const modules = this.cnfJson.modules;
      const getModuleCadNoName = (pno: number) => modules?.find(f => f.id === pno)?.cadNoName;

      staticProcesses.forEach(e => {
        const cadNoName = getModuleCadNoName(e.ObjInfo?.ProcessoNo);

        if (cadNoName) {
          e.ObjInfo.AtividadeNo = selectedTenant[cadNoName] || null;
        } else {
          alert(`cadNoName não configurado no config.json para o processo: ${e.ObjInfo.ProcessoNo}`);
        }
      });
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'fillDerivedFormId', error.message);
    }
  }

  /**
   * Deixa a primeira letra maiúscula, e o restante minúsculo. Exceção para siglas, que permanecem todas em maiúsculo.
   * @param str String a ser convertida.
   * @returns String convertida. Exemplo: "exemplo de processo" => "Exemplo de processo". "SIGLA" => "SIGLA".
   */
  protected firstUpperCase(str: string): string {
    try {
      const siglas = ['SIGLA'];

      if (siglas.includes(str.toUpperCase())) {
        return str.toUpperCase();
      }

      return this.glb.primeiraMaiuscula(str);
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'firstUpperCase', error.message);
    }

    return str;
  }

  /**
   * Ordena o array de processos pelo valor numérico do ComplementaryVar1, mas preserva os elementos que têm ComplementaryVar1 nulo ou indefinido na posição original. Ou seja, os elementos com ComplementaryVar1 válido são ordenados entre si, mas os elementos com ComplementaryVar1 nulo ou indefinido permanecem na mesma posição, independentemente da ordenação dos outros elementos.
   * @param arr Array de processos a ser ordenado.
   * @returns Array de processos ordenado pelo valor numérico do ComplementaryVar1, mas com os elementos que têm ComplementaryVar1 nulo ou indefinido preservados na posição original.
   */
  private sortByComplementaryVar1PreserveNulls(arr: any[]): any[] {
    const withValue = arr
      .map((item, index) => ({ item, index }))
      .filter(({ item }) => item.ComplementaryVar1 != null);

    withValue.sort((a, b) => a.item.ComplementaryVar1 - b.item.ComplementaryVar1);

    const result = [...arr];

    let i = 0;
    for (let pos = 0; pos < arr.length; pos++) {
      if (arr[pos].ComplementaryVar1 != null) {
        result[pos] = withValue[i].item;
        i++;
      }
    }

    return result;
  }
}
